import React, { useMemo } from 'react'
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Typography,
  Collapse,
  Tooltip,
} from '@mui/material'
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  RestoreFromTrash as ReactivateIcon,
} from '@mui/icons-material'
import type { Class, ClassSchedule, ClassSectionWithRelations } from '@timetable/types'
import type { SortConfig } from '../shared'
import { EmptyState } from '../EmptyState'

interface ClassListProps {
  classes: Class[]
  sections: ClassSectionWithRelations[]
  searchQuery: string
  sortConfig: SortConfig
  schedules?: ClassSchedule[]
  showInactive?: boolean
  onEditClass: (classItem: Class) => void
  onDeleteClass: (classItem: Class, hasTimetableRecords: boolean) => void
  onReactivateClass: (classItem: Class) => void
  onEditSection: (section: ClassSectionWithRelations) => void
  onDeleteSection: (section: ClassSectionWithRelations) => void
  onAddSection: (classId: string) => void
}

const CLASS_GROUP_COLORS: Record<string, 'primary' | 'secondary' | 'success'> = {
  Primary: 'primary',
  Middle: 'secondary',
  Secondary: 'success',
}

export const ClassList: React.FC<ClassListProps> = ({
  classes,
  sections,
  searchQuery,
  sortConfig,
  schedules = [],
  showInactive = false,
  onEditClass,
  onDeleteClass,
  onReactivateClass,
  onEditSection,
  onDeleteSection,
  onAddSection,
}) => {
  const [expandedClasses, setExpandedClasses] = React.useState<Set<string>>(new Set())

  const toggleClass = (classId: string) => {
    setExpandedClasses((prev) => {
      const next = new Set(prev)
      if (next.has(classId)) {
        next.delete(classId)
      } else {
        next.add(classId)
      }
      return next
    })
  }

  const getSectionsForClass = (classId: string) => {
    return sections.filter((s) => s.class_id === classId)
  }

  // Filter and sort classes
  const processedClasses = useMemo(() => {
    let filtered = [...classes]

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter((classItem) => {
        const nameMatch = classItem.class_name.toLowerCase().includes(query)
        const groupMatch = classItem.class_group.toLowerCase().includes(query)
        return nameMatch || groupMatch
      })
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue: string | number = ''
      let bValue: string | number = ''

      switch (sortConfig.field) {
        case 'class_name':
          aValue = a.class_name.toLowerCase()
          bValue = b.class_name.toLowerCase()
          break
        case 'class_group':
          aValue = a.class_group.toLowerCase()
          bValue = b.class_group.toLowerCase()
          break
        case 'section_count':
          aValue = getSectionsForClass(a.id).length
          bValue = getSectionsForClass(b.id).length
          break
        default:
          return 0
      }

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1
      return 0
    })

    return filtered
  }, [classes, sections, searchQuery, sortConfig])

  if (processedClasses.length === 0) {
    return (
      <EmptyState
        title="No classes found"
        description={
          searchQuery
            ? 'Try adjusting your search criteria'
            : 'No classes available'
        }
      />
    )
  }

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell width="50px" />
            <TableCell>Class</TableCell>
            <TableCell>Class Group</TableCell>
            <TableCell>Class Schedule</TableCell>
            <TableCell>Sections</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {processedClasses.map((classItem) => {
            const classSections = getSectionsForClass(classItem.id)
            const isExpanded = expandedClasses.has(classItem.id)
            const isInactive = !classItem.is_active
            const schedule = schedules.find(s => s.id === classItem.class_schedule_id)

            return (
              <React.Fragment key={classItem.id}>
                <TableRow
                  hover
                  sx={isInactive ? { opacity: 0.5 } : undefined}
                >
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => toggleClass(classItem.id)}
                      disabled={classSections.length === 0}
                    >
                      {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body1" fontWeight="medium">
                      {classItem.class_name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={classItem.class_group}
                      size="small"
                      color={CLASS_GROUP_COLORS[classItem.class_group] ?? 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    {schedule
                      ? <Tooltip title={schedule.description ?? ''} placement="top">
                          <Chip label={schedule.name} size="small" color="secondary" variant="outlined" />
                        </Tooltip>
                      : <Typography variant="caption" color="text.disabled">None</Typography>
                    }
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={`${classSections.length} section${classSections.length !== 1 ? 's' : ''}`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right">
                    {isInactive ? (
                      <Tooltip title="Reactivate Class">
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() => onReactivateClass(classItem)}
                        >
                          <ReactivateIcon />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <>
                        <IconButton
                          size="small"
                          onClick={() => onEditClass(classItem)}
                          title="Edit Class"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => onAddSection(classItem.id)}
                          title="Add Section"
                        >
                          <AddIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => onDeleteClass(classItem, classSections.length > 0)}
                          title="Delete Class"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </>
                    )}
                  </TableCell>
                </TableRow>

                {/* Sections Row */}
                <TableRow>
                  <TableCell colSpan={6} sx={{ py: 0, borderBottom: 'none' }}>
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <Box sx={{ py: 2, pl: 6 }}>
                        {classSections.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            No sections yet. Click the + button to add one.
                          </Typography>
                        ) : (
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Section</TableCell>
                                <TableCell>Class Teacher</TableCell>
                                <TableCell align="right">Actions</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {classSections.map((section) => (
                                <TableRow key={section.id}>
                                  <TableCell>
                                    <Typography variant="body2" fontWeight="medium">
                                      {classItem.class_name}-{section.section}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2">
                                      {section.class_teacher?.name || 'Not assigned'}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    <IconButton
                                      size="small"
                                      onClick={() => onEditSection(section)}
                                      title="Edit Section"
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => onDeleteSection(section)}
                                      title="Delete Section"
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        )}
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            )
          })}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
